import * as THREE from 'three';
import type { ExplosiveSpec } from '../game/weapons';

// Ballistic projectiles (OXIDE_SPEC.md section 3). The owner chose real bullets over hitscan: a
// round leaves the muzzle at high speed toward the resolved aim point, falls under gravity, and is
// stepped through the world as a real moving segment so cover, trees, rocks, terrain, and height
// all block it naturally. Distant shots land low, so you lead and compensate.
//
// Everything is POOLED. Projectiles live in a fixed ring buffer (no per-shot allocation); tracers
// are one InstancedMesh (a unit box stretched along each live round's velocity); impacts are a
// small pool of additive flash sprites that fade. Add `group` to the scene once; call fire() to
// shoot and update(dt, hitTest) each frame. The caller supplies the hitTest (it knows terrain +
// builder colliders + nodes + targets) and an onHit callback for damage.

// All tunables. Centralized per the project rule (no inline magic numbers). Muzzle speed is very
// fast but tuned for the game's world scale; gravity gives a readable drop over distance.
export const BALLISTICS = {
  maxProjectiles: 128, // ring-buffer pool size (oldest is recycled if exceeded)
  muzzleSpeed: 400, // m/s at the muzzle (~1.8x faster than before -> flatter, snappier rounds, less lead)
  gravity: 9.8, // downward accel on the round (m/s^2); the arc that forces leading distant shots
  lifetime: 2.5, // seconds before a round despawns if it never hits
  maxRange: 600, // hard cull distance from the muzzle
  stepMax: 4.0, // max world units advanced per collision sub-step (long frames are subdivided)
  // Tracer streak: a bright box stretched along the velocity behind the round's nose.
  tracerLength: 6.0, // length of the streak (world units), clamped to how far the round actually moved
  tracerWidth: 0.06, // cross-section of the streak
  tracerColor: 0xffd9a0,
  tracerOpacity: 0.85,
  // Impact flash: a brief additive sprite at the hit point.
  impactPool: 32,
  impactSize: 0.55, // sprite size at the start of the flash
  impactGrow: 1.8, // size multiplier reached as it fades
  impactLife: 0.16, // seconds the flash lasts
  impactColor: 0xfff0c0,
  // AK fire (consumed by the caller's fire-rate gate + spread cone, kept here so all weapon feel
  // lives in one block).
  akRatePerSec: 9.5, // rounds per second held (full auto)
  // Playtest 2026-07-13 ("very accurate, needs way more recoil"): wider cones + a scaled-up spray.
  akSpreadDeg: 1.5, // hip-fire cone half-angle (degrees)
  akAdsSpreadDeg: 0.35, // cone while aiming down sights (ALT held): tight but no longer a laser
  // Owner 2026-07-13 round 2 ("it just needs to kick up more -- should be somewhat hard to master"):
  // the KICK-UP is the skill check, not the spread. ~1.8 deg/shot early -> a full uncontrolled mag
  // climbs the view WAY off target; you learn to drag down through the S-curve like Rust.
  akClimbScale: 3.6, // multiplier on the AK_RECOIL table's vertical climb (tune the whole spray at once)
  akDriftScale: 2.2, // multiplier on its horizontal S-drift
  gunClimbScale: 2.4, // vertical-kick multiplier for every NON-AK gun (def.recoil / def.recoilPattern pitches)
  // Recoil pattern (applied to the camera/aim each shot): a vertical climb then a horizontal drift, Rust-style.
  // Recovers smoothly between bursts. Strength scales down while ADS.
  recoilClimb: 0.32, // degrees of upward pitch kick per shot (into the SMOOTHED target, not the camera directly)
  recoilHoriz: 0.16, // degrees of horizontal sway per shot (sign alternates + wanders into a curve)
  recoilRecover: 22.0, // deg/sec the recoil TARGET eases back toward zero between bursts (snappy return)
  recoilAdsScale: 0.6, // recoil multiplier while aiming down sights (tighter)
  // 3RD-PERSON recoil = the AIM-DOT model (owner 2026-07-14, proven on 41d21f8). The camera never kicks
  // in 3P; the accumulated recoil raises the AIM RAY (cursor ray) BEFORE aim resolution, and the reticle
  // marks exactly where bullets will land. You fight it 1:1 by pulling the cursor down -- the same hand
  // motion as 1st person -- and the caps make the drift PLATEAU: a full mag-dump parks the marker at the
  // pitch cap and holds; it can never ratchet off to the sky.
  recoil3pPitchCap: 6.0, // deg: max accumulated upward aim offset in 3rd person (the spray plateau)
  recoil3pYawCap: 2.5, // deg: max accumulated horizontal sway in 3rd person
  recoil3pBurstRecover: 1.5, // deg/sec the offset drains back even MID-burst (recoilRecover takes over on release)
  akDamage: 45, // per hit. A gun hits HARD -- the AK is the heaviest hitter (a few rounds drop most things)
} as const;

// What a fired round carries beyond its motion. owner lets the caller skip self-hits; damage is
// passed straight back through onHit so the caller can apply falloff with the impact distance.
export interface FireOpts {
  owner?: unknown; // the shooter entity (so the world hitTest can ignore it); opaque to ballistics
  damage?: number; // forwarded to onHit untouched (the caller applies any falloff)
  speed?: number; // override muzzle speed (default BALLISTICS.muzzleSpeed)
  explosive?: ExplosiveSpec; // explosive round (e.g. explosive 5.56): on impact the caller's onExplode detonates here
  visual?: boolean; // networked REMOTE shot: draw the tracer + impact but fire NO onHit/onExplode (the shooter's client owns the damage), so a relayed shot is a pure visible streak with zero double-damage
}

// Filled by the caller's hitTest when a segment p0->p1 hits something solid.
export interface HitResult {
  point: THREE.Vector3; // world hit point
  normal: THREE.Vector3; // surface normal at the hit (for the impact flash orientation)
  entity: unknown; // whatever the world supplies: a target id, or null for static cover
}

// The world collision probe the caller provides. Tests the segment p0->p1; on a hit it fills `out`
// and returns true. Returning false means the segment is clear. ballistics owns no world knowledge.
export type HitTest = (p0: THREE.Vector3, p1: THREE.Vector3, out: HitResult) => boolean;

// Called once when a round connects: entity is out.entity (a target id, or null for static cover),
// point is the world hit point, dir the round's normalized flight direction at impact (so a static
// hit can be re-cast against building pieces for wall chip + the soft-side rule). The caller
// applies damage (with falloff) here. dir is a reused scratch vector -- read it, don't keep it.
export type OnHit = (entity: unknown, point: THREE.Vector3, damage: number, dir: THREE.Vector3) => void;

interface Projectile {
  alive: boolean;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  age: number;
  travelled: number; // total distance flown, for max-range cull
  damage: number;
  owner: unknown;
  explosive: ExplosiveSpec | null; // non-null -> detonate at the impact point (explosive ammo)
  visual: boolean; // relayed remote shot: tracer only, no hit callbacks
}

interface Impact {
  alive: boolean;
  age: number;
}

const TRACER_GEO = new THREE.BoxGeometry(1, 1, 1); // unit box, scaled per-tracer into a streak
const UP = new THREE.Vector3(0, 1, 0);

export class Ballistics {
  readonly group = new THREE.Group();

  private readonly pool: Projectile[] = [];
  private next = 0; // ring-buffer write head

  private readonly tracers: THREE.InstancedMesh;
  private readonly tracerDummy = new THREE.Object3D();
  private readonly hiddenScale = new THREE.Vector3(0, 0, 0);

  private readonly impacts: Impact[] = [];
  private readonly impactSprites: THREE.Sprite[] = [];

  private onHit: OnHit | null = null;
  private onExplode: ((point: THREE.Vector3, spec: ExplosiveSpec, dir: THREE.Vector3) => void) | null = null;

  // Scratch vectors so update()/fire() never allocate.
  private readonly p0 = new THREE.Vector3();
  private readonly p1 = new THREE.Vector3();
  private readonly hit: HitResult = { point: new THREE.Vector3(), normal: new THREE.Vector3(), entity: null };
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpMid = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();

  constructor(onHit?: OnHit) {
    this.onHit = onHit ?? null;

    for (let i = 0; i < BALLISTICS.maxProjectiles; i++) {
      this.pool.push({ alive: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), age: 0, travelled: 0, damage: 0, owner: null, explosive: null, visual: false });
    }

    const tracerMat = new THREE.MeshBasicMaterial({
      color: BALLISTICS.tracerColor,
      transparent: true,
      opacity: BALLISTICS.tracerOpacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.tracers = new THREE.InstancedMesh(TRACER_GEO, tracerMat, BALLISTICS.maxProjectiles);
    this.tracers.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.tracers.frustumCulled = false; // streaks are tiny and move fast; skip per-frame culling math
    this.tracers.renderOrder = 4;
    // Park every tracer at zero scale until a projectile drives it.
    this.tracerDummy.position.set(0, 0, 0);
    this.tracerDummy.scale.copy(this.hiddenScale);
    this.tracerDummy.updateMatrix();
    for (let i = 0; i < BALLISTICS.maxProjectiles; i++) this.tracers.setMatrixAt(i, this.tracerDummy.matrix);
    this.tracers.instanceMatrix.needsUpdate = true;
    this.group.add(this.tracers);

    const impactMat = makeImpactMaterial();
    for (let i = 0; i < BALLISTICS.impactPool; i++) {
      const s = new THREE.Sprite(impactMat.clone());
      s.visible = false;
      s.renderOrder = 5;
      this.group.add(s);
      this.impactSprites.push(s);
      this.impacts.push({ alive: false, age: 0 });
    }
  }

  setOnHit(cb: OnHit | null): void {
    this.onHit = cb;
  }

  // Detonation hook for explosive rounds: called at the impact point of any round fired with
  // opts.explosive, with the round's normalized flight direction (for the direct-hit piece re-cast).
  setOnExplode(cb: ((point: THREE.Vector3, spec: ExplosiveSpec, dir: THREE.Vector3) => void) | null): void {
    this.onExplode = cb;
  }

  // Spawn one round from (origin) along (dir) at muzzle speed. dir need not be normalized. Reuses
  // a pooled projectile (recycles the oldest slot if the pool is saturated). No allocation.
  fire(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    opts?: FireOpts,
  ): void {
    const p = this.pool[this.next];
    this.next = (this.next + 1) % this.pool.length;

    const len = Math.hypot(dx, dy, dz) || 1;
    const speed = opts?.speed ?? BALLISTICS.muzzleSpeed;
    p.pos.set(ox, oy, oz);
    p.vel.set((dx / len) * speed, (dy / len) * speed, (dz / len) * speed);
    p.age = 0;
    p.travelled = 0;
    p.damage = opts?.damage ?? 0;
    p.owner = opts?.owner ?? null;
    p.explosive = opts?.explosive ?? null;
    p.visual = opts?.visual ?? false;
    p.alive = true;
  }

  // Advance every live round and resolve hits against the world. hitTest tests each motion segment
  // (subdivided so a fast round cannot tunnel through thin cover); on a hit we flash an impact,
  // invoke onHit(entity, point, damage), and despawn the round. Rounds also die on lifetime/range.
  update(dt: number, hitTest: HitTest): void {
    let tracerDirty = false;

    for (let i = 0; i < this.pool.length; i++) {
      const p = this.pool[i];
      if (!p.alive) continue;

      p.age += dt;
      if (p.age >= BALLISTICS.lifetime) { this.kill(p, i); tracerDirty = true; continue; }

      // Integrate this frame, but march the segment in sub-steps no longer than stepMax so a fast
      // round is collision-tested continuously (no tunneling through a thin wall or a tree trunk).
      const fullLen = p.vel.length() * dt;
      const steps = Math.max(1, Math.ceil(fullLen / BALLISTICS.stepMax));
      const sub = dt / steps;
      let hitSomething = false;

      for (let s = 0; s < steps; s++) {
        this.p0.copy(p.pos);
        // Semi-implicit Euler: apply gravity to velocity, then advance position by this sub-step.
        p.vel.y -= BALLISTICS.gravity * sub;
        p.pos.addScaledVector(p.vel, sub);
        this.p1.copy(p.pos);
        p.travelled += this.p0.distanceTo(this.p1);

        if (hitTest(this.p0, this.p1, this.hit)) {
          this.spawnImpact(this.hit.point, this.hit.normal);
          this.tmpDir.copy(p.vel).normalize(); // flight direction at impact (callbacks read it synchronously)
          // A relayed remote shot draws its streak + impact spark but NEVER applies damage here (the
          // shooter's client already resolved the hit + sent the pvp op); this keeps it a pure visual.
          if (!p.visual) {
            if (this.onHit) this.onHit(this.hit.entity, this.hit.point, p.damage, this.tmpDir);
            if (p.explosive && this.onExplode) this.onExplode(this.hit.point, p.explosive, this.tmpDir); // explosive round bursts on impact
          }
          this.kill(p, i);
          hitSomething = true;
          tracerDirty = true;
          break;
        }

        if (p.travelled >= BALLISTICS.maxRange) {
          this.kill(p, i);
          hitSomething = true;
          tracerDirty = true;
          break;
        }
      }

      if (!hitSomething) { this.writeTracer(i, p); tracerDirty = true; }
    }

    if (tracerDirty) this.tracers.instanceMatrix.needsUpdate = true;
    this.tickImpacts(dt);
  }

  // Orient + stretch tracer instance i into a streak trailing the round's nose along its velocity.
  private writeTracer(i: number, p: Projectile): void {
    const speed = p.vel.length() || 1;
    this.tmpDir.copy(p.vel).multiplyScalar(1 / speed);
    // Streak length is the tracer length, but never longer than the distance flown so far (a round
    // just out of the muzzle has a short streak, not a long one poking back behind the gun).
    const length = Math.min(BALLISTICS.tracerLength, Math.max(p.travelled, BALLISTICS.tracerWidth));
    // Center the box half a length behind the nose so the leading face sits at the round.
    this.tmpMid.copy(p.pos).addScaledVector(this.tmpDir, -length * 0.5);
    this.tmpQuat.setFromUnitVectors(UP, this.tmpDir); // box's local +Y becomes the travel axis
    this.tracerDummy.position.copy(this.tmpMid);
    this.tracerDummy.quaternion.copy(this.tmpQuat);
    this.tracerDummy.scale.set(BALLISTICS.tracerWidth, length, BALLISTICS.tracerWidth);
    this.tracerDummy.updateMatrix();
    this.tracers.setMatrixAt(i, this.tracerDummy.matrix);
  }

  private kill(p: Projectile, i: number): void {
    p.alive = false;
    // Park the tracer at zero scale so the dead round leaves no streak.
    this.tracerDummy.position.set(0, 0, 0);
    this.tracerDummy.quaternion.identity();
    this.tracerDummy.scale.copy(this.hiddenScale);
    this.tracerDummy.updateMatrix();
    this.tracers.setMatrixAt(i, this.tracerDummy.matrix);
  }

  // Light up the first idle impact sprite at the hit point. The normal nudges it a hair off the
  // surface so the additive flash is not z-fought by the thing it hit.
  private spawnImpact(point: THREE.Vector3, normal: THREE.Vector3): void {
    for (let i = 0; i < this.impacts.length; i++) {
      const im = this.impacts[i];
      if (im.alive) continue;
      im.alive = true;
      im.age = 0;
      const spr = this.impactSprites[i];
      spr.position.copy(point).addScaledVector(normal, 0.02);
      spr.scale.setScalar(BALLISTICS.impactSize);
      (spr.material as THREE.SpriteMaterial).opacity = 1;
      spr.visible = true;
      return;
    }
    // Pool saturated: silently drop the flash (the round still applied its hit).
  }

  private tickImpacts(dt: number): void {
    for (let i = 0; i < this.impacts.length; i++) {
      const im = this.impacts[i];
      if (!im.alive) continue;
      im.age += dt;
      const t = im.age / BALLISTICS.impactLife;
      const spr = this.impactSprites[i];
      if (t >= 1) {
        im.alive = false;
        spr.visible = false;
        continue;
      }
      const grow = 1 + (BALLISTICS.impactGrow - 1) * t;
      spr.scale.setScalar(BALLISTICS.impactSize * grow);
      (spr.material as THREE.SpriteMaterial).opacity = 1 - t;
    }
  }
}

// A soft round additive flash (white-hot core, warm falloff) for the bullet impact sprite.
function makeImpactMaterial(): THREE.SpriteMaterial {
  const size = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.35, 'rgba(255,240,192,0.9)');
  g.addColorStop(1, 'rgba(255,200,120,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.SpriteMaterial({
    map: tex,
    color: BALLISTICS.impactColor,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
  });
}
